import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Textarea from '@salesforce/design-system-react/components/textarea';
import SettingsRow from '../SettingsRow';

import { GET_USER_EMAIL_CONFIGURATION } from '../../../graphql/getUserEmailConfiguration';
import { SAVE_USER_EMAIL_CONFIGURATION } from '../../../graphql/mutations/saveUserEmailConfiguration';

const TOOLTIP_MESSAGES = {
  CONTACT_DETAILS: 'Sales Rep contact details best for ASR and RSM',
  ORDER_DETAILS:
    'Adds Account #, Name, Order C#, PO #, Surgery Date, Surgeon, Procedure, Patient ID to the email.',
  BILL_SHEET_ATTACHMENT: `Adds 'Attached is the bill sheet from Name, Procedure, Surgery Date.'`,
  REP_SIGNATURE: `Includes order's Sales Rep contact information at the bottom of the email.`,
} as const;

export type UserEmailConfiguration = {
  id?: number;
  userId?: string;
  customEmailBody?: string;
  contactDetailsInfo?: boolean;
  orderDetailsInfo?: boolean;
  repSignature?: boolean;
  addBillSheetAttachment?: boolean;
  firstName?: string;
  lastName?: string;
  title?: string;
  business?: string;
  department?: string;
  city?: string;
  phone?: string;
  emailLink?: string;
  hyperlink?: string;
  createdAt?: string;
  updatedAt?: string;
};

const EMPTY: UserEmailConfiguration = {
  customEmailBody: '',
  contactDetailsInfo: false,
  orderDetailsInfo: false,
  repSignature: false,
  addBillSheetAttachment: false,
  firstName: '',
  lastName: '',
  title: '',
  business: '',
  department: '',
  city: '',
  phone: '',
  emailLink: '',
  hyperlink: '',
};

export function useEmailSettingsController() {
  const initialRef = React.useRef<UserEmailConfiguration | null>(null);
  const [settings, setSettings] = React.useState<UserEmailConfiguration | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const { loading, error, refetch } = useQuery(GET_USER_EMAIL_CONFIGURATION, {
    fetchPolicy: 'cache-and-network',
    onCompleted: d => {
      if (initialRef.current) return;
      const server = (d?.getUserEmailConfiguration ?? {}) as UserEmailConfiguration;
      const seeded = { ...EMPTY, ...server };
      initialRef.current = seeded;
      setSettings(seeded);
    },
  });

  const update = <K extends keyof UserEmailConfiguration>(k: K, v: UserEmailConfiguration[K]) =>
    setSettings(s => (s ? { ...s, [k]: v } : s));

  const isDirty = React.useMemo(() => {
    if (!settings || !initialRef.current) return false;
    return JSON.stringify(settings) !== JSON.stringify(initialRef.current);
  }, [settings]);

  const [mutate, { loading: saving }] = useMutation(SAVE_USER_EMAIL_CONFIGURATION);

  const save = React.useCallback(async () => {
    if (!settings) return;

    setErrorMessage(null);
    
    const input = {
      customEmailBody: settings.customEmailBody ?? '',
      contactDetailsInfo: !!settings.contactDetailsInfo,
      orderDetailsInfo: !!settings.orderDetailsInfo,
      repSignature: settings.repSignature ?? '',
      addBillSheetAttachment: !!settings.addBillSheetAttachment,
      firstName: settings.firstName ?? '',
      lastName: settings.lastName ?? '',
      title: settings.title ?? '',
      business: settings.business ?? '',
      department: settings.department ?? '',
      city: settings.city ?? '',
      phone: settings.phone ?? '',
      emailLink: settings.emailLink ?? '',
      hyperlink: settings.hyperlink ?? '',
    };

    try {
      const result = await mutate({
        variables: { input },
        update(cache, { data }) {
          const config = data?.saveUserEmailConfiguration?.configuration;
          if (!config) return;
          cache.writeQuery({
            query: GET_USER_EMAIL_CONFIGURATION,
            data: { getUserEmailConfiguration: config },
          });
        },
      });

      if (result?.data?.saveUserEmailConfiguration?.success) {
        initialRef.current = settings;
        await refetch();
      } else {
        if (initialRef.current) {
          setSettings(initialRef.current);
        }
        const errorMsg = result?.data?.saveUserEmailConfiguration?.message || 'Failed to save user configuration';
        setErrorMessage(errorMsg);
      }
    } catch (error) {
      if (initialRef.current) {
        setSettings(initialRef.current);
      }
      setErrorMessage('An error occurred while saving configuration');
    }
  }, [mutate, settings, refetch]);

  const reset = React.useCallback(() => {
    if (initialRef.current) setSettings(initialRef.current);
  }, []);

  return {
    loading: loading && !settings,
    error,
    settings: settings ?? EMPTY,
    update,
    isDirty,
    saving,
    save,
    reset,
    errorMessage,
  };
}

export function EmailSettingsContactContent(props: {
  settings: UserEmailConfiguration;
  update: <K extends keyof UserEmailConfiguration>(k: K, v: UserEmailConfiguration[K]) => void;
  errorMessage?: string | null;
}) {
  const { settings, update, errorMessage } = props;

  return (
    <>
      {errorMessage && (
        <div
          style={{
            padding: '10px',
            marginBottom: '10px',
            borderRadius: '4px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
          }}
        >
          {errorMessage}
        </div>
      )}
      <SettingsRow
        id="rep-signature"
        label="Rep Signature"
        tooltip={TOOLTIP_MESSAGES.REP_SIGNATURE}
        description="Includes rep signature"
        showToggle
        toggleChecked={!!settings.repSignature}
        onToggle={v => update('repSignature', v)}
      />
      {settings.repSignature && (
        <>
          <SettingsRow
            id="first-name"
            label="First Name"
            tooltip=""
            inputValue={settings.firstName ?? ''}
            onInputChange={v => update('firstName', v)}
            inputPlaceholder=""
          />
          <SettingsRow
            id="last-name"
            label="Last Name"
            tooltip=""
            inputValue={settings.lastName ?? ''}
            onInputChange={v => update('lastName', v)}
            inputPlaceholder=""
          />
          <SettingsRow
            id="title"
            label="Title"
            tooltip=""
            inputValue={settings.title ?? ''}
            onInputChange={v => update('title', v)}
            inputPlaceholder=""
          />
          <SettingsRow
            id="business"
            label="Business"
            tooltip=""
            inputValue={settings.business ?? ''}
            onInputChange={v => update('business', v)}
            inputPlaceholder=""
          />
          <SettingsRow
            id="department"
            label="Department"
            tooltip=""
            inputValue={settings.department ?? ''}
            onInputChange={v => update('department', v)}
            inputPlaceholder=""
          />
          <SettingsRow
            id="city"
            label="City"
            tooltip=""
            inputValue={settings.city ?? ''}
            onInputChange={v => update('city', v)}
            inputPlaceholder=""
          />
          <SettingsRow
            id="phone"
            label="Phone"
            tooltip=""
            inputValue={settings.phone ?? ''}
            onInputChange={v => update('phone', v)}
            inputPlaceholder=""
          />
          <SettingsRow
            id="email"
            label="Email"
            tooltip=""
            inputValue={settings.emailLink ?? ''}
            onInputChange={v => update('emailLink', v)}
            inputPlaceholder=""
          />
          <SettingsRow
            id="hyperlink"
            label="URL"
            tooltip=""
            inputValue={settings.hyperlink ?? ''}
            onInputChange={v => update('hyperlink', v)}
            inputPlaceholder=""
          />
        </>
      )}
    </>
  );
}

export function EmailSettingsBodyContent(props: {
  settings: UserEmailConfiguration;
  update: <K extends keyof UserEmailConfiguration>(k: K, v: UserEmailConfiguration[K]) => void;
}) {
  const { settings, update } = props;

  return (
    <>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          fontWeight: 600,
          fontSize: 14,
          marginBottom: '10px',
        }}
      >
        Email Settings
      </div>

      <div id="notif-email-body-label">Custom Email Body</div>

      <>
        <Textarea
          aria-labelledby="notif-email-body-label"
          value={settings.customEmailBody ?? ''}
          placeholder="Type the email body here..."
          rows={6}
          onChange={(e: any, data?: { value?: string }) =>
            update('customEmailBody', data?.value ?? e?.target?.value ?? '')
          }
          style={{ width: '100%' }}
        />
        <SettingsRow
          id="contact-details"
          label="Contact Details"
          showDescription
          tooltip={TOOLTIP_MESSAGES.CONTACT_DETAILS}
          description="Adds sales rep contact details, best used by ASRs, and RSMs since the information is similar to the email signature."
          showToggle
          toggleChecked={!!settings.contactDetailsInfo}
          onToggle={v => update('contactDetailsInfo', v)}
        />
        <SettingsRow
          id="order-details"
          label="Order Details"
          showDescription
          tooltip={TOOLTIP_MESSAGES.ORDER_DETAILS}
          description="Adds the Order Information - Account #, Name, Order C#, PO #, Surgery Date, Surgeon, Procedure, Patient ID to the end of the email."
          showToggle
          toggleChecked={!!settings.orderDetailsInfo}
          onToggle={v => update('orderDetailsInfo', v)}
        />
        <SettingsRow
          id="bill-attachment"
          label="Attached is the bill sheet from"
          showDescription
          tooltip={TOOLTIP_MESSAGES.BILL_SHEET_ATTACHMENT}
          description="Adds 'Attached is the bill sheet from Surgeon Name, Procedure, Surgery Date.' to the email body."
          showToggle
          toggleChecked={!!settings.addBillSheetAttachment}
          onToggle={v => update('addBillSheetAttachment', v)}
        />
      </>
    </>
  );
}
